// ─── Response Card Style (RSVP adapter) ────────────────────────────────────
// Phase RSVP Style 3.0 — three-layer architecture:
//
//   Background layer (NEW)  → large RSVP block; controlled by bgLayer* fields
//   Surface layer            → inner card/glass/vellum; controlled by cardSurface*
//   Form column              → content; controlled by cardWidthPercent
//
// Surface fields kept verbatim from 2.x → existing invitations unchanged.
// Background fields all default to "inactive" (`bgLayerType: 'none'`) → no
// large block is rendered unless the designer opts in.

import { getSurfaceMaterialStyle, withAlpha } from './surfaceMaterial';

export { withAlpha };

// ─── Surface (inner card) scalar readers ───────────────────

export function getSurfaceAlpha(cs) {
  const v = cs?.cardSurfaceOpacity;
  if (typeof v !== 'number' || Number.isNaN(v)) return 1;
  return Math.max(0, Math.min(100, v)) / 100;
}

export function getSurfaceShadowStrength(cs) {
  const v = cs?.cardSurfaceShadowStrength;
  if (typeof v !== 'number' || Number.isNaN(v)) return 0;
  return Math.max(0, Math.min(100, v));
}

export function getBackdropBlur(cs) {
  const v = cs?.cardBackdropBlur;
  if (typeof v !== 'number' || Number.isNaN(v)) return 0;
  return Math.max(0, Math.min(30, v));
}

export function getBackdropSaturate(cs) {
  const v = cs?.cardBackdropSaturate;
  if (typeof v !== 'number' || Number.isNaN(v)) return 100;
  return Math.max(100, Math.min(200, v));
}

// `surfaceType: 'none' | 'color'`. Default behaviour: visible (color) so
// existing invitations keep their card.
export function isSurfaceVisible(cs) {
  return cs?.surfaceType !== 'none';
}

// ─── Background-layer scalar readers (NEW in 3.0) ──────────

// `bgLayerType: 'none' | 'color'`. Default: 'none' → background layer is
// inactive and not rendered.
export function isBackgroundLayerActive(cs) {
  return cs?.bgLayerType === 'color';
}

export function getBgLayerAlpha(cs) {
  const v = cs?.bgLayerOpacity;
  if (typeof v !== 'number' || Number.isNaN(v)) return 1;
  return Math.max(0, Math.min(100, v)) / 100;
}

export function getBgLayerShadowStrength(cs) {
  const v = cs?.bgLayerShadowStrength;
  if (typeof v !== 'number' || Number.isNaN(v)) return 0;
  return Math.max(0, Math.min(100, v));
}

export function getBgLayerBackdropBlur(cs) {
  const v = cs?.bgLayerBackdropBlur;
  if (typeof v !== 'number' || Number.isNaN(v)) return 0;
  return Math.max(0, Math.min(30, v));
}

export function getBgLayerBackdropSaturate(cs) {
  const v = cs?.bgLayerBackdropSaturate;
  if (typeof v !== 'number' || Number.isNaN(v)) return 100;
  return Math.max(100, Math.min(200, v));
}

// Background layer corner radius (px). Default null → variant decides
// (RsvpSurfaceShell uses borderRadiusRem prop, ~20-32px). When the designer
// sets a value via the slider, it wins. 0..64 px range.
export function getBgLayerRadiusPx(cs) {
  const v = cs?.bgLayerRadius;
  if (typeof v !== 'number' || Number.isNaN(v)) return null;
  return Math.max(0, Math.min(64, v));
}

// Surface (inner card) corner radius (px). Same null-fallback semantic as bg.
export function getSurfaceRadiusPx(cs) {
  const v = cs?.cardSurfaceRadius;
  if (typeof v !== 'number' || Number.isNaN(v)) return null;
  return Math.max(0, Math.min(64, v));
}

// ─── Width readers ─────────────────────────────────────────

function readWidthPercent(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) return 100;
  return Math.max(70, Math.min(100, value));
}

export function getBgLayerWidthPercent(cs) {
  return readWidthPercent(cs?.bgLayerWidthPercent);
}

export function getSurfaceWidthPercent(cs) {
  return readWidthPercent(cs?.cardSurfaceWidthPercent);
}

export function getFormWidthPercent(cs) {
  return readWidthPercent(cs?.cardWidthPercent);
}

/**
 * Width style for the inner FORM column.
 * Returns null at 100% so the variant's existing inner width applies.
 */
export function getContentWidthStyle(cs) {
  const pct = getFormWidthPercent(cs);
  if (pct >= 100) return null;
  return { width: `${pct}%`, maxWidth: '100%' };
}

// ─── Legacy aliases ────────────────────────────────────────

export const getCardWidthPercent = getFormWidthPercent;
export const getCardWidthStyle = getContentWidthStyle;

export const getSurfaceWidthStyle = (cs) => {
  const pct = getSurfaceWidthPercent(cs);
  if (pct >= 100) return null;
  return { width: `${pct}%`, maxWidth: '100%' };
};

export function getSurfaceBorderColor(baseColor, surfaceAlpha) {
  if (surfaceAlpha >= 1) return baseColor;
  return withAlpha(baseColor, surfaceAlpha);
}

// ─── Convenience builders ──────────────────────────────────

export function getRsvpSurfaceStyle(cs, { backgroundColor, borderColor } = {}) {
  return getSurfaceMaterialStyle({
    surfaceOpacity: cs?.cardSurfaceOpacity,
    surfaceShadowStrength: cs?.cardSurfaceShadowStrength,
    backdropBlur: cs?.cardBackdropBlur,
    backdropSaturate: cs?.cardBackdropSaturate,
    backgroundColor,
    borderColor,
  });
}

export function getRsvpBackgroundLayerStyle(cs, { backgroundColor, borderColor } = {}) {
  return getSurfaceMaterialStyle({
    surfaceOpacity: cs?.bgLayerOpacity,
    surfaceShadowStrength: cs?.bgLayerShadowStrength,
    backdropBlur: cs?.bgLayerBackdropBlur,
    backdropSaturate: cs?.bgLayerBackdropSaturate,
    backgroundColor,
    borderColor,
  });
}
